
import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const categories = ['All', 'E-commerce', 'Hospitality', 'Health & Wellness', 'Fashion', 'Food & Beverage'];

const caseStudies = [
  {
    id: 1,
    title: 'Luxe Botanica',
    category: 'E-commerce',
    description: 'Organic skincare brand that needed to establish a premium presence on social media.',
    image: 'https://public.readdy.ai/ai/img_res/726d7db9e3a2887910138ff72419f999.jpg',
    results: { followers: '+340%', engagement: '+180%', sales: '+250%' },
    tags: ['Instagram', 'Content Strategy', 'Influencer Marketing']
  },
  {
    id: 2,
    title: 'The Harbour Hotel',
    category: 'Hospitality',
    description: 'Boutique coastal hotel looking to attract younger travelers and increase direct bookings.',
    image: 'https://readdy.ai/api/search-image?query=Stunning%20boutique%20coastal%20hotel%20with%20ocean%20views%20featuring%20modern%20luxury%20interior%20design%20with%20natural%20textures%20and%20warm%20ambient%20lighting%20showcasing%20premium%20hospitality%20experience&width=600&height=400&seq=case2&orientation=landscape',
    results: { followers: '+420%', engagement: '+220%', sales: '+180%' },
    tags: ['TikTok', 'UGC Campaign', 'Paid Ads']
  },
  {
    id: 3,
    title: 'FitFlow Studio',
    category: 'Health & Wellness',
    description: 'Yoga and pilates studio chain expanding their online community and class bookings.',
    image: 'https://readdy.ai/api/search-image?query=Modern%20yoga%20studio%20interior%20with%20natural%20light%20streaming%20through%20large%20windows%20showing%20minimalist%20design%20with%20plants%20and%20wooden%20elements%20creating%20peaceful%20wellness%20atmosphere&width=600&height=400&seq=case3&orientation=landscape',
    results: { followers: '+280%', engagement: '+350%', sales: '+190%' },
    tags: ['Instagram Reels', 'Community Building', 'Email Integration']
  },
  {
    id: 4,
    title: 'Velvet & Thread',
    category: 'Fashion',
    description: 'Sustainable fashion brand targeting eco-conscious millennials and Gen Z shoppers.',
    image: 'https://readdy.ai/api/search-image?query=Sustainable%20fashion%20clothing%20collection%20displayed%20in%20minimalist%20boutique%20setting%20with%20natural%20fabrics%20and%20earth%20tones%20showcasing%20eco%20friendly%20style%20with%20modern%20aesthetic&width=600&height=400&seq=case4&orientation=landscape',
    results: { followers: '+510%', engagement: '+290%', sales: '+320%' },
    tags: ['Pinterest', 'Influencer Collab', 'Sustainability Content']
  },
  {
    id: 5,
    title: 'Ember & Oak',
    category: 'Food & Beverage',
    description: 'Farm-to-table restaurant group wanting to showcase their culinary story and local partnerships.',
    image: 'https://readdy.ai/api/search-image?query=Artisanal%20farm%20to%20table%20restaurant%20interior%20with%20rustic%20wooden%20tables%20and%20fresh%20ingredients%20displayed%20beautifully%20with%20warm%20lighting%20creating%20cozy%20dining%20atmosphere&width=600&height=400&seq=case5&orientation=landscape',
    results: { followers: '+380%', engagement: '+410%', sales: '+220%' },
    tags: ['Instagram Stories', 'Video Content', 'Local SEO']
  },
  {
    id: 6,
    title: 'Glow Aesthetics',
    category: 'Health & Wellness',
    description: 'Medical spa clinic building trust and educating potential clients about treatments.',
    image: 'https://readdy.ai/api/search-image?query=Luxurious%20medical%20spa%20treatment%20room%20with%20modern%20equipment%20and%20calming%20neutral%20tones%20featuring%20clean%20minimalist%20design%20with%20soft%20ambient%20lighting%20creating%20professional%20wellness%20environment&width=600&height=400&seq=case6&orientation=landscape',
    results: { followers: '+260%', engagement: '+190%', sales: '+280%' },
    tags: ['Educational Content', 'Before/After', 'Trust Building']
  }
];

export default function CaseStudiesGrid() {
  const [activeFilter, setActiveFilter] = useState('All');
  const { ref, isVisible } = useScrollAnimation();

  const filteredStudies = activeFilter === 'All' 
    ? caseStudies 
    : caseStudies.filter(study => study.category === activeFilter);

  return (
    <section className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div
          ref={ref}
          className={`flex flex-wrap justify-center gap-3 mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveFilter(category)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeFilter === category
                  ? 'bg-slate-900 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredStudies.map((study, index) => (
            <CaseStudyCard key={study.id} study={study} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}

interface CaseStudy {
  id: number;
  title: string;
  category: string;
  description: string;
  image: string;
  results: { followers: string; engagement: string; sales: string };
  tags: string[];
}

function CaseStudyCard({ study, index }: { study: CaseStudy; index: number }) {
  const { ref, isVisible } = useScrollAnimation();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      ref={ref}
      className={`group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 cursor-pointer ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative h-56 overflow-hidden">
        <img
          src={study.image}
          alt={study.title}
          className={`w-full h-full object-cover object-top transition-transform duration-700 ${
            isHovered ? 'scale-110' : 'scale-100'
          }`}
        />
        <div className={`absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-transparent transition-opacity duration-300 ${
          isHovered ? 'opacity-100' : 'opacity-70'
        }`}></div>
        
        <span className="absolute top-4 right-4 px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-medium rounded-full">
          {study.category}
        </span>

        <div className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ${
          isHovered ? 'opacity-100' : 'opacity-0'
        }`}>
          <span className="px-6 py-3 bg-[#00989e] text-white text-sm font-semibold rounded-full hover:bg-[#007a7f] transition-colors whitespace-nowrap">
            View Case Study
          </span>
        </div>

        <div className="absolute bottom-4 left-4 right-4">
          <h3 className="text-xl font-bold text-white mb-1">{study.title}</h3>
        </div>
      </div>

      <div className="p-6">
        <p className="text-slate-600 text-sm mb-4 line-clamp-2">{study.description}</p>
        
        <div className="flex gap-4 mb-4">
          <div className="text-center">
            <p className="text-lg font-bold text-[#00989e]">{study.results.followers}</p>
            <p className="text-xs text-slate-500">Followers</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-[#00989e]">{study.results.engagement}</p>
            <p className="text-xs text-slate-500">Engagement</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-[#00989e]">{study.results.sales}</p>
            <p className="text-xs text-slate-500">Sales</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {study.tags.map((tag) => (
            <span key={tag} className="px-2.5 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
